import CollapsibleCardContext from "components/shared/components/CollapsibleCard/context/CollapsibleCardContext";
import { useCollapsibleCard } from "components/shared/components/CollapsibleCard/context/useCollapsibleCard";
import { useAutoHeight } from "components/shared/components/CollapsibleCard/hooks/useAuthHeight";
import { cloneElement, ReactElement, ReactNode, useMemo, useState } from "react";

type RootProps = {
  children: ReactNode;
  className?: string;

  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;

  defaultExpanded?: boolean;
};

const Root = ({
  children,
  className,
  expanded: controlledExpanded,
  onExpandedChange,
  defaultExpanded = true,
}: RootProps) => {
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState(defaultExpanded);

  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : uncontrolledExpanded;

  const toggleExpanded = () => {
    if (isControlled) {
      onExpandedChange && onExpandedChange(!controlledExpanded);
    } else {
      setUncontrolledExpanded(!uncontrolledExpanded);
    }
  };

  const value = useMemo(() => ({ expanded, toggleExpanded }), [expanded]);
  return (
    <CollapsibleCardContext.Provider value={value}>
      <div className={className}>{children}</div>
    </CollapsibleCardContext.Provider>
  );
};

type SectionProps = {
  children: ReactNode;
  className?: string;
};

const Header = ({ children, className }: SectionProps) => {
  return <div className={className}>{children}</div>;
};

const Body = ({
  children,
  className,
  contentClassName,
}: SectionProps & { contentClassName?: string }) => {
  const { expanded } = useCollapsibleCard();
  const { ref, style } = useAutoHeight(expanded);

  return (
    <div
      className={className}
      style={style}
    >
      <div
        ref={ref}
        className={contentClassName}
      >
        {children}
      </div>
    </div>
  );
};

type SlotProps = {
  children: ReactElement;
  [key: string]: any;
};

const Slot = ({ children, ...props }: SlotProps) => {
  return cloneElement(children, {
    ...props,
    ...children.props,
  });
};

type TriggerProps = {
  children: ReactElement;
  asChild?: boolean;
};

const Trigger = ({ children, asChild = false }: TriggerProps) => {
  const { expanded, toggleExpanded } = useCollapsibleCard();

  const triggerProps = {
    "aria-expanded": expanded,
    onClick: (e: React.MouseEvent) => {
      children.props.onClick?.(e);
      toggleExpanded();
    },
  };

  if (asChild) {
    return <Slot {...triggerProps}>{children}</Slot>;
  }
  return <button {...triggerProps}>{children}</button>;
};

export const CollapsibleCard = Object.assign(Root, {
  Header,
  Body,
  Trigger,
});
